<script setup lang="ts">
import logoUrl from "shared-ui/assets/logo.svg";
import WebsiteHeaderLinks from "shared-ui/components/layout/WebsiteHeaderLinks.vue";
import { onMounted, onUnmounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const isOpen = ref<boolean>(false);
const isScrolled = ref<boolean>(false);

function onScroll(): void {
  isScrolled.value = window.scrollY > 30;
}

function setBodyScrollLock(locked: boolean): void {
  if (typeof document === "undefined") return;
  document.body.style.overflow = locked ? "hidden" : "";
}

watch(isOpen, (open) => {
  setBodyScrollLock(open);
});

onMounted(() => {
  window.addEventListener("scroll", onScroll, { passive: true });
  onScroll();
});

onUnmounted(() => {
  window.removeEventListener("scroll", onScroll);
  setBodyScrollLock(false);
});

const appBase: string = import.meta.env.VITE_PUBLIC_APP_SITE_URL ?? "";
const loginCTALink: string = `${appBase}?login=true&utm_source=website&utm_medium=navbar`;
const signupCTALink: string = `${appBase}?utm_source=website&utm_medium=navbar`;

withDefaults(
  defineProps<{
    homeLink: string;
    productLink: string;
    pricingLink: string;
    partnersLink: string;
    companyLink: string;
    securityLink: string;
    hideMenuItems?: boolean;
  }>(),
  {
    hideMenuItems: false,
  },
);

function toggleMenu(): void {
  isOpen.value = !isOpen.value;
}
</script>

<template>
  <header
    class="sticky top-0 z-50 mb-20 w-full border-b transition-website-header duration-300 ease-website-header"
    :class="
      isScrolled
        ? 'border-black/8 bg-white/72 backdrop-blur-website-header'
        : 'border-transparent bg-transparent'
    "
  >
    <div
      class="flex h-19 w-full items-center justify-between gap-4 px-4 md:px-8 lg:grid lg:grid-cols-website-header 2xl:px-16"
    >
      <div class="inline-flex shrink-0 items-center lg:justify-self-start">
        <a :href="homeLink">
          <img
            :src="logoUrl"
            :alt="t('common.logo_alt')"
            class="block h-9 w-auto shrink-0"
            width="136"
            height="34"
          />
        </a>
      </div>

      <!-- Desktop Menu -->
      <nav
        v-if="!hideMenuItems"
        class="z-200 hidden shrink-0 flex-row items-center gap-5 text-primary-black lg:inline-flex xl:gap-8 2xl:gap-10"
      >
        <WebsiteHeaderLinks
          :product-link="productLink"
          :pricing-link="pricingLink"
          :partners-link="partnersLink"
          :company-link="companyLink"
          :security-link="securityLink"
        />
      </nav>
      <div v-else></div>

      <div class="shrink-0 lg:justify-self-end">
        <!-- CTA (desktop) -->
        <div class="hidden flex-row items-center gap-5 lg:flex 2xl:gap-6">
          <slot name="cta-desktop" />
          <a
            class="text-base font-medium whitespace-nowrap text-primary-black transition-colors duration-200 hover:text-primary-black/70"
            :href="loginCTALink"
          >
            {{ t("common.login") }}
          </a>
          <a
            v-if="!hideMenuItems"
            class="inline-flex flex-col items-center justify-center rounded-pill bg-primary-black px-3 py-2 font-medium whitespace-nowrap text-primary-white lg:px-6 lg:py-4"
            :href="signupCTALink"
            target="_blank"
          >
            {{ t("cta.start_trial") }}
          </a>
        </div>

        <div class="inline-flex translate-y-0.5 items-center gap-4 lg:hidden">
          <!-- Bare language toggle (44px tap target) -->
          <div class="inline-flex h-11 items-center">
            <slot name="cta-mobile" />
          </div>

          <!-- Menu pill: hamburger icon + label -->
          <button
            v-if="!hideMenuItems"
            class="inline-flex h-11 cursor-pointer items-center gap-2 rounded-full border border-black/15 px-4 text-base font-medium text-primary-black transition-colors duration-200 hover:bg-black/5 focus:outline-none"
            :aria-label="t('website_header.toggle_menu')"
            :aria-expanded="isOpen"
            aria-controls="mobile-nav-overlay"
            @click="toggleMenu"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="size-5"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </svg>
            {{ t("website_navigation.menu") }}
          </button>
        </div>
      </div>
    </div>
  </header>

  <!-- Mobile overlay menu (teleported to body) -->
  <teleport to="body">
    <nav
      v-show="isOpen"
      id="mobile-nav-overlay"
      class="fixed inset-0 z-9999 flex flex-col bg-white text-primary-black lg:hidden"
    >
      <!-- Top bar: logo + language + close -->
      <div class="flex h-19 shrink-0 items-center justify-between px-4 md:px-8">
        <a :href="homeLink" @click="isOpen = false">
          <img
            :src="logoUrl"
            :alt="t('common.logo_alt')"
            class="block h-9 w-auto shrink-0"
            width="136"
            height="34"
          />
        </a>
        <div class="inline-flex translate-y-0.5 items-center gap-4">
          <div class="inline-flex h-11 items-center">
            <slot name="cta-mobile" />
          </div>
          <button
            class="inline-flex h-11 cursor-pointer items-center gap-2 rounded-full border border-black/15 px-4 text-base font-medium text-primary-black transition-colors duration-200 hover:bg-black/5 focus:outline-none"
            :aria-label="t('website_header.close_menu')"
            @click="toggleMenu"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="size-5"
              viewBox="0 0 24 24"
              stroke="currentColor"
              stroke-width="2"
              fill="none"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
            {{ t("website_header.close") }}
          </button>
        </div>
      </div>

      <!-- Scrollable content -->
      <div class="flex flex-1 flex-col overflow-y-auto px-4 pb-8 md:px-8">
        <div v-if="!hideMenuItems" class="flex flex-col">
          <WebsiteHeaderLinks
            mobile
            :product-link="productLink"
            :pricing-link="pricingLink"
            :partners-link="partnersLink"
            :company-link="companyLink"
            :security-link="securityLink"
            @link-click="isOpen = false"
          />
        </div>

        <!-- Pinned full-width CTAs -->
        <div class="mt-auto flex flex-col gap-3 pt-8">
          <a
            class="inline-flex h-14 items-center justify-center rounded-full border border-black/20 text-base font-semibold text-primary-black transition-colors duration-200 hover:bg-black/5"
            :href="loginCTALink"
            @click="isOpen = false"
          >
            {{ t("common.login") }}
          </a>
          <a
            v-if="!hideMenuItems"
            class="inline-flex h-14 items-center justify-center rounded-full bg-primary-black text-base font-semibold text-primary-white transition-colors duration-200"
            :href="signupCTALink"
            target="_blank"
            @click="isOpen = false"
          >
            {{ t("cta.start_trial") }}
          </a>
        </div>
      </div>
    </nav>
  </teleport>
</template>
